import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {API} from "../../../types/API";
import {AxiosError, AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import OutlineInput from "../../../components/OutlineInput";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {setErrors, validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {emailRegex, nameRegEx} from "./WithdrawsForm";
import {Input} from "../../../types/Input";
import {useTranslation} from "react-i18next";
import DropdownInput from "../../../components/DropdownInput";
import {FooterListItem} from "../../../components/Sidebar/FooterMenu";
import {AwePayBanksResponse, WithdrawCountryResponse} from "../../../types/Responses/WithdrawsResponse";
import CountryAvatar from "../../../components/CountryAvatar";
import {CountryShort} from "../../../types/Responses/CountriesResponse";
import {APILink} from "../../../types/APILink";
import {useSystemContext} from "../../../types/contexts/SystemContext";

interface AwePayFormProps {
    broker: number,
    clientTransactionID?: number
}

const AwePayForm = ({broker, clientTransactionID}: AwePayFormProps) => {

    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const system = useSystemContext();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [currency, setCurrency] = useState<string>();
    const [accountCurrency, setAccountCurrency] = useState<number>();
    const [country, setCountry] = useState<number>();
    const [bankProvince, setBankProvince] = useState<string>();
    const [bankCity, setBankCity] = useState<string>();
    const [accountNumber, setAccountNumber] = useState<string>();
    const [accountName, setAccountName] = useState<string>();
    const [bank, setBank] = useState<string>();

    const [bankList, setBankList] = useState<AwePayBanksResponse>();
    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [countriesList, setCountriesList] = useState<WithdrawCountryResponse>();

    const dispatch = useDispatch();

    const clearData = () => {
        setInProgress(false);
        setAmount(0);
        setFirstName('');
        setLastName('');
        setEmail('');
        setCurrency('');
        setBankProvince('');
        setBankCity('');
        setAccountNumber('');
        setAccountName('');
        setBank('');
        setAccountCurrency(undefined);
        setCountry(undefined);
        setValidated(false)
    }

    const getCountries = useCallback(() => {
        API.getWithdrawCountries(broker, WithdrawProvider.AWEPAY).then((response: AxiosResponse) => {
            setCountriesList(response.data as WithdrawCountryResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_COUNTRIES}`, toast, handleError));
    }, [toast, handleError]);

    const getCurrencyAccount = useCallback(() => {
        API.getAwePayBanks().then((response: AxiosResponse) => {
            setBankList(response.data);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_AWEPAY_BANKS}`, toast, handleError));
    }, [toast, handleError]);

    const getClientTransaction = useCallback(() => {
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.first_name);
            setLastName(response.data.last_name);
            setEmail(response.data.email);
            setCurrency(response.data.currency.code);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`, toast, handleError));
    }, [toast, handleError]);

    const changeCountry = useCallback((u: string | number) => {
        setCountry(typeof u === "string" ? parseInt(u) : u);
        setValidated(false);
    }, [setCountry]);

    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeBankProvince = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankProvince(e.currentTarget.value)
        }
    }, [setValidated, setBankProvince]);

    const changeBankCity = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankCity(e.currentTarget.value)
        }
    }, [setValidated, setBankCity]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);


    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changeAccountNumber = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountNumber(e.currentTarget.value)
        }
    }, [setValidated, setAccountNumber]);

    const changeAccountName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountName(e.currentTarget.value)
        }
    }, [setValidated, setAccountName]);

    const changeCurrency = useCallback((s: string | number) => {
        if (!bankList || !system || !system.system || !system.system.currencies)
            return;
        setValidated(false);
        const c = typeof s === "number" ? s : parseInt(s);
        setAccountCurrency(c);
        setCurrency(system.system.currencies.find(i => i.id === s)?.code)

    }, [setValidated, setAccountCurrency, setCurrency, bankList, system]);

    const changeBank = useCallback((s: string | number) => {
        if (!bankList)
            return;
        setValidated(false);
        setBank(s as string);

    }, [setValidated, bankList, setBank]);


    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = t('admin.withdraws.approve_is_required')
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.withdraws.created_message')}<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [toast, clearData]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            const request = {
                bank,
                country,
                amount,
                email,
                currency: accountCurrency,
                broker,
                provider: WithdrawProvider.AWEPAY,
                first_name: firstName,
                last_name: lastName,
                bank_province: bankProvince,
                bank_city: bankCity,
                account_number: accountNumber,
                account_name: accountName,
            };
            API.makeAwePayWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    if (e instanceof AxiosError && e.response) {
                        setErrors(e.response.data.errors);
                    }
                    ErrorHandler(e, `post${APILink.MAKE_AWEPAY_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [firstName, country, currency, accountCurrency, lastName, validated, amount, email, accountName, accountNumber, bankCity, bankProvince, broker, clientTransactionID, bank]);

    const submit = useCallback(() => {
        setInProgress(true);
        const inputs: Input[] = [
            {key: 'country', label: t('admin.withdraws.country'), type: InputType.number, regex: '^\\d{1,3}$'},
            {key: 'account', label: t('admin.withdraws.currency'), type: InputType.number},
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^\\d{1,18}$'},
            {key: 'first_name', label: t('admin.withdraws.first_name'), type: InputType.number, regex: nameRegEx},
            {key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.number, regex: nameRegEx},
            {key: 'email', label: t('admin.withdraws.email'), type: InputType.email, regex: emailRegex},
            {key: 'bank', label: t('admin.withdraws.bank'), type: InputType.input},
            {key: 'bank_city', label: t('admin.withdraws.bank_city'), type: InputType.input},
            {key: 'bank_province', label: t('admin.withdraws.bank_province'), type: InputType.input},
            {key: 'account_number', label: t('admin.withdraws.account_number'), type: InputType.input},
            {key: 'account_name', label: t('admin.withdraws.account_name'), type: InputType.input},
        ];

        const data = {
            bank,
            country,
            amount,
            first_name: firstName,
            last_name: lastName,
            email,
            account: accountCurrency,
            bank_city: bankCity,
            bank_province: bankProvince,
            account_number: accountNumber,
            account_name: accountName,
        };

        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [amount, country, accountCurrency, currency, firstName, lastName, email, callback, bankCity, bankProvince, accountName, accountNumber, bank]);


    const amountUI = useMemo(() => {
        if (!currency || !bank)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency})} type="number" validated={validated}
                          value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [validated, changeAmount, amount, currency, bank]);

    const firstNameUI = useMemo(() => {
        if (!bank)
            return <></>
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [validated, firstName, changeFirstName, bank])

    const lastNameUI = useMemo(() => {
        if (!bank)
            return <></>
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [validated, lastName, changeLastName, bank])

    const emailUI = useMemo(() => {
        if (!bank)
            return <></>
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [validated, email, changeEmail, bank])

    const countryUI = useMemo(() => {
        if (!countriesList)
            return <></>;

        return <div className="max-w-md mt-4">
            <DropdownInput
                list={countriesList.data.map((c) => {
                    return {
                        name: c.name, id: c.id, icon: <CountryAvatar country={c as CountryShort} withName={false}
                                                                     className="h-3 max-w-[1rem]"/>
                    }
                })}
                name={t('admin.withdraws.country')} selected={country} searchable={true} onChange={changeCountry}/>
        </div>
    }, [country, changeCountry, countriesList]);

    const currencyUI = useMemo(() => {
        if (!bankList || !country || !countriesList || !system || !system.system || !system.system.currencies)
            return <></>;

        const countryCode = countriesList.data.find(c => c.id === country);
        if (!countryCode)
            return <></>
        const allowedCurrencies = bankList.data.find(b => b.countries.includes(countryCode.code))
        if (!allowedCurrencies)
            return <></>;
        const currencies: FooterListItem[] = system.system.currencies.filter(c => allowedCurrencies.currencies.includes(c.code)).map((c) => {
            return {id: c.id, code: c.code, name: c.code};
        });

        return <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.currency')} className="w-full min-w-[200px]"
                           selected={accountCurrency}
                           onChange={changeCurrency}
                           list={currencies}/>
        </div>
    }, [validated, bankList, accountCurrency, changeCurrency, country, countriesList])

    const bankUI = useMemo(() => {
        if (!bankList || !country || !countriesList || !system || !currency)
            return <></>;

        const countryCode = countriesList.data.find(c => c.id === country);
        if (!countryCode)
            return <></>
        const allowedBanks = bankList.data.find(b => b.countries.includes(countryCode.code))
        if (!allowedBanks)
            return <></>;

        return <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.bank')} className="w-full min-w-[200px]"
                           selected={bank}
                           onChange={changeBank}
                           list={allowedBanks.banks}/>
        </div>
    }, [validated, bankList, changeBank, country, bank, currency, countriesList])


    const bankProvinceUI = useMemo(() => {
        if (!bank)
            return <></>
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_province')} type="text" validated={validated}
                          value={bankProvince}
                          fieldName="bank_province"
                          onChange={changeBankProvince}/>
        </div>
    }, [validated, bankProvince, changeBankProvince,bank])


    const bankCityUI = useMemo(() => {
        if (!bank)
            return <></>
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_city')} type="text" validated={validated} value={bankCity}
                          fieldName="bank_city"
                          onChange={changeBankCity}/>
        </div>
    }, [validated, bankCity, changeBankCity, bank])

    const accountNumberUI = useMemo(() => {
        if (!bank)
            return <></>
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_number')} type="text" validated={validated}
                          value={accountNumber}
                          fieldName="account_number"
                          onChange={changeAccountNumber}/>
        </div>
    }, [validated, accountNumber, changeAccountNumber, bank])

    const accountNameUI = useMemo(() => {
        if (!bank)
            return <></>
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_name')} type="text" validated={validated} value={accountName}
                          fieldName="account_name"
                          onChange={changeAccountName}/>
        </div>
    }, [validated, accountName, changeAccountName, bank])


    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit]);


    useEffect(() => {
        clearData();
        getCurrencyAccount();
        getCountries();
        if (clientTransactionID) {
            getClientTransaction();
        }
    }, [broker, clientTransactionID]);

    return <>
        {countryUI}
        {currencyUI}
        {bankUI}
        {bankProvinceUI}
        {bankCityUI}
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {accountNumberUI}
        {accountNameUI}
        {amountUI}
        {submitButtonUI}
    </>
}

export default memo(AwePayForm);
